import {
  VibeSessionClient,
  type AccessTokenResponse,
  type VibeDeviceAccount,
  type VibeProfile as BaseVibeProfile,
} from "@vibe/sdk";
import type { CredentialPresentationV1 } from "../shared/credentials";
import {
  LiveEventTerminalError,
  runLiveEventLoop,
  type LiveUpdateStatus,
} from "./live-events";

export { completePopupCallback, VibePopupBlockedError } from "@vibe/sdk";
export type { VibeDeviceAccount } from "@vibe/sdk";
export type { LiveUpdateStatus } from "./live-events";

export interface VibeProfile extends BaseVibeProfile {
  avatarDataUri: string | null;
  coverDataUri: string | null;
  profileUpdatedAt: string | null;
}

export interface StoredValue<T> {
  key: string;
  value: T;
  version: number;
  updatedAt: string;
}

export interface VibeAppOnboardingData {
  values: Record<string, string>;
  updatedAt: string;
}

export interface Subscription {
  unsubscribe(): void;
}

export type VibeScope =
  | "profile:read"
  | "onboarding:read"
  | "credentials.present"
  | "storage"
  | "collections.read"
  | "collections.write"
  | "calendar.read"
  | "calendar.sync"
  | "feed.publish"
  | `content.read:${string}`
  | `content.write:${string}`
  | `semantic.search:${string}`
  | `service.use:${string}`;

export interface ContentDocument<T = unknown> {
  id: string;
  templateId: string;
  templateSchemaSha256: string;
  entityId: string;
  ownerDid: string;
  appOrigin: string;
  payload: T | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  previousContentId: string | null;
  deleted: boolean;
}

export interface ContentPage<T = unknown> {
  documents: Array<ContentDocument<T>>;
  offset: number;
  limit: number;
  hasMore: boolean;
}

export interface ContentCollection {
  id: string;
  ownerDid: string;
  name: string;
  templateId: string;
  entityIds: string[];
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface SemanticServiceStatus {
  service: "openai.embeddings";
  active: boolean;
  available: boolean;
  provider: "openai";
  model: string;
  dimensions: number;
  indexed: number;
  pending: number;
  failed: number;
  lastVerifiedAt: string | null;
  lastUsedAt: string | null;
  lastError: string | null;
}

export interface ContentSearchResult<T = unknown> {
  document: ContentDocument<T>;
  score: number;
  lexicalScore: number | null;
  semanticScore: number | null;
}

export interface ContentSearchResponse<T = unknown> {
  mode: "lexical" | "hybrid" | "semantic";
  fallbackReason: "providerNotConfigured" | "providerUnavailable" | null;
  warning: "indexingInProgress" | null;
  results: Array<ContentSearchResult<T>>;
  offset: number;
  limit: number;
  hasMore: boolean;
  indexed: number;
  pending: number;
}

export interface SemanticApi {
  status(options: { templateId: string }): Promise<SemanticServiceStatus>;
  lexical<T>(options: { templateId: string; query: string; collectionId?: string; offset?: number; limit?: number }): Promise<ContentSearchResponse<T>>;
  search<T>(options: { templateId: string; query: string; collectionId?: string; offset?: number; limit?: number }): Promise<ContentSearchResponse<T>>;
  related<T>(options: { templateId: string; entityId: string; collectionId?: string; offset?: number; limit?: number }): Promise<ContentSearchResponse<T>>;
}

export interface CalendarStatus {
  service: "google.calendar";
  configured: boolean;
  credentialStorageAvailable: boolean;
  connected: boolean;
  needsReconnect: boolean;
  selectedCalendarCount: number;
  calendarCount: number;
  eventCount: number;
  lastSyncAt: string | null;
  lastError: string | null;
}

export interface CalendarSummary {
  id: string;
  externalId: string;
  summary: string;
  description: string | null;
  timeZone: string | null;
  primary: boolean;
  included: boolean;
}

export interface CalendarEvent {
  id: string;
  calendarId: string;
  externalId: string;
  updatedAt: string | null;
  status: string;
  summary: string | null;
  description: string | null;
  location: string | null;
  timeZone: string | null;
  allDay: boolean;
  startDate: string | null;
  endDate: string | null;
  startAt: string | null;
  endAt: string | null;
  recurringEventId: string | null;
}

export interface CalendarApi {
  status(): Promise<CalendarStatus>;
  calendars(): Promise<CalendarSummary[]>;
  events(options?: { from?: string; to?: string; limit?: number }): Promise<CalendarEvent[]>;
  refresh(): Promise<CalendarStatus>;
}

export interface ContentApi {
  query<T>(options: { templateId: string; collectionId?: string; offset?: number; limit?: number }): Promise<ContentPage<T>>;
  getDocument<T>(options: { templateId: string; contentId: string }): Promise<ContentDocument<T>>;
  getEntity<T>(options: { templateId: string; entityId: string }): Promise<ContentDocument<T>>;
  save<T>(options: { templateId: string; entityId?: string; payload: T; version?: number }): Promise<ContentDocument<T>>;
  delete(options: { templateId: string; entityId: string; version: number }): Promise<ContentDocument<never>>;
  subscribe<T>(
    options: { templateId: string; collectionId?: string; offset?: number; limit?: number },
    callback: (page: ContentPage<T>) => void | Promise<void>,
    onStatus?: (status: LiveUpdateStatus) => void,
  ): Promise<Subscription>;
}

export interface CollectionApi {
  list(options: { templateId: string }): Promise<ContentCollection[]>;
  create(options: { name: string; templateId: string; entityIds?: string[] }): Promise<ContentCollection>;
  rename(options: { collectionId: string; name: string; version: number }): Promise<ContentCollection>;
  setMembership(options: {
    collectionId: string;
    templateId: string;
    entityIds: string[];
    included: boolean;
    version: number;
  }): Promise<ContentCollection>;
  delete(options: { collectionId: string; version: number }): Promise<void>;
  subscribe(
    options: { templateId: string },
    callback: (collections: ContentCollection[]) => void | Promise<void>,
    onStatus?: (status: LiveUpdateStatus) => void,
  ): Promise<Subscription>;
}

export class VersionConflictError extends Error {
  readonly currentVersion: number;

  constructor(currentVersion: number) {
    super("The node has a newer version.");
    this.name = "VersionConflictError";
    this.currentVersion = currentVersion;
  }
}

export class VibeSdk {
  readonly nodeOrigin: string;
  readonly appOrigin: string;
  readonly content: ContentApi;
  readonly collections: CollectionApi;
  readonly semantic: SemanticApi;
  readonly calendar: CalendarApi;
  private readonly session: VibeSessionClient;

  constructor(options: { nodeOrigin: string }) {
    this.session = new VibeSessionClient(options);
    this.nodeOrigin = this.session.nodeOrigin;
    this.appOrigin = this.session.appOrigin;
    this.content = {
      query: <T>(queryOptions: { templateId: string; collectionId?: string; offset?: number; limit?: number }) => this.queryContent<T>(queryOptions),
      getDocument: <T>(queryOptions: { templateId: string; contentId: string }) => this.getContentDocument<T>(queryOptions),
      getEntity: <T>(queryOptions: { templateId: string; entityId: string }) => this.getContentEntity<T>(queryOptions),
      save: <T>(queryOptions: { templateId: string; entityId?: string; payload: T; version?: number }) => this.saveContent<T>(queryOptions),
      delete: (queryOptions: { templateId: string; entityId: string; version: number }) => this.deleteContent(queryOptions),
      subscribe: <T>(
        queryOptions: { templateId: string; collectionId?: string; offset?: number; limit?: number },
        callback: (page: ContentPage<T>) => void | Promise<void>,
        onStatus?: (status: LiveUpdateStatus) => void,
      ) => this.subscribeContent(queryOptions, callback, onStatus),
    };
    this.collections = {
      list: (queryOptions) => this.listCollections(queryOptions),
      create: (queryOptions) => this.createCollection(queryOptions),
      rename: (queryOptions) => this.renameCollection(queryOptions),
      setMembership: (queryOptions) => this.setCollectionMembership(queryOptions),
      delete: (queryOptions) => this.deleteCollection(queryOptions),
      subscribe: (queryOptions, callback, onStatus) => this.subscribeCollections(queryOptions, callback, onStatus),
    };
    this.semantic = {
      status: (queryOptions) => this.semanticStatus(queryOptions),
      lexical: <T>(queryOptions: { templateId: string; query: string; collectionId?: string; offset?: number; limit?: number }) => this.lexicalSearch<T>(queryOptions),
      search: <T>(queryOptions: { templateId: string; query: string; collectionId?: string; offset?: number; limit?: number }) => this.semanticSearch<T>(queryOptions),
      related: <T>(queryOptions: { templateId: string; entityId: string; collectionId?: string; offset?: number; limit?: number }) => this.relatedContent<T>(queryOptions),
    };
    this.calendar = {
      status: () => this.calendarStatus(),
      calendars: () => this.calendarList(),
      events: (queryOptions) => this.calendarEvents(queryOptions),
      refresh: () => this.calendarRefresh(),
    };
  }

  async connect(scope: VibeScope): Promise<AccessTokenResponse> {
    return this.session.connect(scope);
  }

  hasToken(scope: VibeScope): boolean {
    return this.session.hasToken(scope);
  }

  async connectMany(scopes: VibeScope[]): Promise<AccessTokenResponse> {
    return this.session.connectMany(scopes);
  }

  async connectManyWithRedirect(scopes: VibeScope[]): Promise<never> {
    return this.session.connectManyWithRedirect(scopes);
  }

  async switchAccount(
    scopes: VibeScope[],
    options: { loginHint?: string; redirect?: boolean } = {},
  ): Promise<AccessTokenResponse> {
    return this.session.switchAccount(scopes, options);
  }

  async completeRedirectCallback(): Promise<boolean> {
    return this.session.completeRedirectCallback();
  }

  currentAccount(): VibeDeviceAccount | null {
    return this.session.currentAccount();
  }

  rememberedAccounts(): VibeDeviceAccount[] {
    return this.session.rememberedAccounts();
  }

  async logout(): Promise<void> {
    return this.session.logout();
  }

  async logoutFromCloud(options: { redirect?: boolean } = {}): Promise<void> {
    return this.session.logoutFromCloud(options);
  }

  async resume(scopes: VibeScope[]): Promise<boolean> {
    return this.session.resume(scopes);
  }

  async profile(): Promise<VibeProfile> {
    return this.session.profile() as Promise<VibeProfile>;
  }

  async onboarding(): Promise<VibeAppOnboardingData> {
    const token = await this.connect("onboarding:read");
    return this.request<VibeAppOnboardingData>("/api/onboarding", token);
  }

  async credentialPresentation(): Promise<CredentialPresentationV1> {
    const token = await this.connect("credentials.present");
    return this.request<CredentialPresentationV1>("/api/credentials/presentation", token);
  }

  async get<T>(key: string): Promise<StoredValue<T> | null> {
    const token = await this.connect("storage");
    const response = await this.authorizedFetch(new URL(`/api/storage/${encodeURIComponent(key)}`, this.nodeOrigin), token);
    if (!response.ok) throw new Error(await responseMessage(response, "Could not read from your node."));
    const stored = (await response.json()) as StoredValue<T> & { found?: boolean };
    return stored.found === false ? null : stored;
  }

  async write<T>(key: string, value: T, version = 0): Promise<{ version: number; updatedAt: string }> {
    const token = await this.connect("storage");
    const response = await this.authorizedFetch(new URL(`/api/storage/${encodeURIComponent(key)}`, this.nodeOrigin), token, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "If-Match": `"${version}"`,
      },
      body: JSON.stringify({ value }),
    });
    if (response.status === 409) {
      const body = (await response.json()) as { currentVersion?: number };
      throw new VersionConflictError(body.currentVersion ?? version);
    }
    if (!response.ok) throw new Error(await responseMessage(response, "Could not save to your node."));
    return (await response.json()) as { version: number; updatedAt: string };
  }

  async read<T>(
    key: string,
    callback: (value: StoredValue<T> | null) => void | Promise<void>,
    onStatus?: (status: LiveUpdateStatus) => void,
  ): Promise<Subscription> {
    const token = await this.connect("storage");
    let active = true;
    let refreshing = false;
    const controller = new AbortController();
    const refresh = async () => {
      if (!active || refreshing) return;
      refreshing = true;
      try {
        await callback(await this.get<T>(key));
      } finally {
        refreshing = false;
      }
    };
    await refresh();
    this.startLiveUpdates(
      token,
      ["storage"],
      (change) => change.collection === "storage" && change.key === key,
      refresh,
      controller.signal,
      onStatus,
    );
    return {
      unsubscribe() {
        active = false;
        controller.abort();
      },
    };
  }

  forgetTokens(): void {
    this.session.forgetTokens();
  }

  private async queryContent<T>(options: { templateId: string; collectionId?: string; offset?: number; limit?: number }): Promise<ContentPage<T>> {
    const token = options.collectionId
      ? await this.connectMany([`content.read:${options.templateId}`, "collections.read"])
      : await this.connect(`content.read:${options.templateId}`);
    const url = new URL("/api/content", this.nodeOrigin);
    url.searchParams.set("templateId", options.templateId);
    if (options.collectionId) url.searchParams.set("collectionId", options.collectionId);
    if (options.offset !== undefined) url.searchParams.set("offset", String(options.offset));
    if (options.limit !== undefined) url.searchParams.set("limit", String(options.limit));
    return this.requestUrl<ContentPage<T>>(url, token);
  }

  private async getContentDocument<T>(options: { templateId: string; contentId: string }): Promise<ContentDocument<T>> {
    const token = await this.connect(`content.read:${options.templateId}`);
    return this.request<ContentDocument<T>>(
      `/api/content/${encodeURIComponent(options.templateId)}/documents/${encodeURIComponent(options.contentId)}`,
      token,
    );
  }

  private async getContentEntity<T>(options: { templateId: string; entityId: string }): Promise<ContentDocument<T>> {
    const token = await this.connect(`content.read:${options.templateId}`);
    return this.request<ContentDocument<T>>(
      `/api/content/${encodeURIComponent(options.templateId)}/entities/${encodeURIComponent(options.entityId)}`,
      token,
    );
  }

  private async saveContent<T>(options: {
    templateId: string;
    entityId?: string;
    payload: T;
    version?: number;
  }): Promise<ContentDocument<T>> {
    const token = await this.connect(`content.write:${options.templateId}`);
    const response = await this.authorizedFetch(new URL(`/api/content/${encodeURIComponent(options.templateId)}`, this.nodeOrigin), token, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "If-Match": `"${options.version ?? 0}"`,
      },
      body: JSON.stringify({ entityId: options.entityId, payload: options.payload }),
    });
    if (response.status === 409) {
      const body = (await response.json()) as { currentVersion?: number };
      throw new VersionConflictError(body.currentVersion ?? options.version ?? 0);
    }
    if (!response.ok) throw new Error(await responseMessage(response, "Could not save content to your node."));
    return (await response.json()) as ContentDocument<T>;
  }

  private async deleteContent(options: {
    templateId: string;
    entityId: string;
    version: number;
  }): Promise<ContentDocument<never>> {
    const token = await this.connect(`content.write:${options.templateId}`);
    const response = await this.authorizedFetch(
      new URL(`/api/content/${encodeURIComponent(options.templateId)}/entities/${encodeURIComponent(options.entityId)}`, this.nodeOrigin),
      token,
      {
        method: "DELETE",
        headers: { "If-Match": `"${options.version}"` },
      },
    );
    if (response.status === 409) {
      const body = (await response.json()) as { currentVersion?: number };
      throw new VersionConflictError(body.currentVersion ?? options.version);
    }
    if (!response.ok) throw new Error(await responseMessage(response, "Could not delete content from your node."));
    return (await response.json()) as ContentDocument<never>;
  }

  private async subscribeContent<T>(
    options: { templateId: string; collectionId?: string; offset?: number; limit?: number },
    callback: (page: ContentPage<T>) => void | Promise<void>,
    onStatus?: (status: LiveUpdateStatus) => void,
  ): Promise<Subscription> {
    const scopes: VibeScope[] = options.collectionId
      ? [`content.read:${options.templateId}`, "collections.read"]
      : [`content.read:${options.templateId}`];
    const token = await this.connectMany(scopes);
    let active = true;
    let refreshing = false;
    const controller = new AbortController();
    const refresh = async () => {
      if (!active || refreshing) return;
      refreshing = true;
      try {
        await callback(await this.queryContent<T>(options));
      } finally {
        refreshing = false;
      }
    };
    await refresh();
    this.startLiveUpdates(
      token,
      scopes,
      (change) => (
        change.collection === "content" && change.templateId === options.templateId
      ) || Boolean(
        options.collectionId &&
        change.collection === "collections" &&
        change.templateId === options.templateId &&
        change.collectionId === options.collectionId
      ),
      refresh,
      controller.signal,
      onStatus,
    );
    return {
      unsubscribe() {
        active = false;
        controller.abort();
      },
    };
  }

  private async semanticStatus(options: { templateId: string }): Promise<SemanticServiceStatus> {
    const token = await this.connect(`content.read:${options.templateId}`);
    const url = new URL("/api/semantic/status", this.nodeOrigin);
    url.searchParams.set("templateId", options.templateId);
    return this.requestUrl<SemanticServiceStatus>(url, token);
  }

  private async calendarStatus(): Promise<CalendarStatus> {
    const token = await this.connect("calendar.read");
    return this.request<CalendarStatus>("/api/calendar/status", token);
  }

  private async calendarList(): Promise<CalendarSummary[]> {
    const token = await this.connect("calendar.read");
    const result = await this.request<{ calendars: CalendarSummary[] }>("/api/calendar/calendars", token);
    return result.calendars;
  }

  private async calendarEvents(options: { from?: string; to?: string; limit?: number } = {}): Promise<CalendarEvent[]> {
    const token = await this.connect("calendar.read");
    const url = new URL("/api/calendar/events", this.nodeOrigin);
    if (options.from) url.searchParams.set("from", options.from);
    if (options.to) url.searchParams.set("to", options.to);
    if (options.limit !== undefined) url.searchParams.set("limit", String(options.limit));
    const result = await this.requestUrl<{ events: CalendarEvent[] }>(url, token);
    return result.events;
  }

  private async calendarRefresh(): Promise<CalendarStatus> {
    const token = await this.connect("calendar.sync");
    return this.requestWithBody<CalendarStatus>("/api/calendar/sync", token, {});
  }

  private async lexicalSearch<T>(options: {
    templateId: string;
    query: string;
    collectionId?: string;
    offset?: number;
    limit?: number;
  }): Promise<ContentSearchResponse<T>> {
    const scopes: VibeScope[] = [`content.read:${options.templateId}`];
    if (options.collectionId) scopes.push("collections.read");
    const token = await this.connectMany(scopes);
    const url = new URL("/api/search", this.nodeOrigin);
    url.searchParams.set("templateId", options.templateId);
    url.searchParams.set("q", options.query);
    if (options.collectionId) url.searchParams.set("collectionId", options.collectionId);
    if (options.offset !== undefined) url.searchParams.set("offset", String(options.offset));
    if (options.limit !== undefined) url.searchParams.set("limit", String(options.limit));
    return this.requestUrl<ContentSearchResponse<T>>(url, token);
  }

  private async semanticSearch<T>(options: {
    templateId: string;
    query: string;
    collectionId?: string;
    offset?: number;
    limit?: number;
  }): Promise<ContentSearchResponse<T>> {
    const scopes: VibeScope[] = [
      `content.read:${options.templateId}`,
      `semantic.search:${options.templateId}`,
      "service.use:openai.embeddings",
    ];
    if (options.collectionId) scopes.push("collections.read");
    const token = await this.connectMany(scopes);
    return this.requestWithBody<ContentSearchResponse<T>>("/api/semantic/search", token, options);
  }

  private async relatedContent<T>(options: {
    templateId: string;
    entityId: string;
    collectionId?: string;
    offset?: number;
    limit?: number;
  }): Promise<ContentSearchResponse<T>> {
    const scopes: VibeScope[] = [
      `content.read:${options.templateId}`,
      `semantic.search:${options.templateId}`,
      "service.use:openai.embeddings",
    ];
    if (options.collectionId) scopes.push("collections.read");
    const token = await this.connectMany(scopes);
    return this.requestWithBody<ContentSearchResponse<T>>("/api/semantic/related", token, options);
  }

  private async requestWithBody<T>(path: string, token: AccessTokenResponse, body: unknown): Promise<T> {
    const response = await this.authorizedFetch(new URL(path, this.nodeOrigin), token, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(await responseMessage(response, "The Vibe node rejected the request."));
    return (await response.json()) as T;
  }

  private async listCollections(options: { templateId: string }): Promise<ContentCollection[]> {
    const token = await this.connect("collections.read");
    const url = new URL("/api/collections", this.nodeOrigin);
    url.searchParams.set("templateId", options.templateId);
    const result = await this.requestUrl<{ collections: ContentCollection[] }>(url, token);
    return result.collections;
  }

  private async createCollection(options: { name: string; templateId: string; entityIds?: string[] }): Promise<ContentCollection> {
    return this.collectionWrite("/api/collections", "POST", options);
  }

  private async renameCollection(options: { collectionId: string; name: string; version: number }): Promise<ContentCollection> {
    return this.collectionWrite(`/api/collections/${encodeURIComponent(options.collectionId)}`, "PATCH", { name: options.name }, options.version);
  }

  private async setCollectionMembership(options: {
    collectionId: string;
    templateId: string;
    entityIds: string[];
    included: boolean;
    version: number;
  }): Promise<ContentCollection> {
    return this.collectionWrite(
      `/api/collections/${encodeURIComponent(options.collectionId)}/members`,
      "PUT",
      { templateId: options.templateId, entityIds: options.entityIds, included: options.included },
      options.version,
    );
  }

  private async deleteCollection(options: { collectionId: string; version: number }): Promise<void> {
    await this.collectionWrite(`/api/collections/${encodeURIComponent(options.collectionId)}`, "DELETE", undefined, options.version);
  }

  private async collectionWrite<T>(path: string, method: "POST" | "PATCH" | "PUT" | "DELETE", body?: unknown, version?: number): Promise<T> {
    const token = await this.connect("collections.write");
    const headers: Record<string, string> = {};
    if (body !== undefined) headers["Content-Type"] = "application/json";
    if (version !== undefined) headers["If-Match"] = `"${version}"`;
    const response = await this.authorizedFetch(new URL(path, this.nodeOrigin), token, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (response.status === 409) {
      const details = (await response.json()) as { currentVersion?: number };
      throw new VersionConflictError(details.currentVersion ?? version ?? 0);
    }
    if (!response.ok) throw new Error(await responseMessage(response, "Could not update the collection."));
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }

  private async subscribeCollections(
    options: { templateId: string },
    callback: (collections: ContentCollection[]) => void | Promise<void>,
    onStatus?: (status: LiveUpdateStatus) => void,
  ): Promise<Subscription> {
    const token = await this.connect("collections.read");
    let active = true;
    let refreshing = false;
    const controller = new AbortController();
    const refresh = async () => {
      if (!active || refreshing) return;
      refreshing = true;
      try { await callback(await this.listCollections(options)); }
      finally { refreshing = false; }
    };
    await refresh();
    this.startLiveUpdates(
      token,
      ["collections.read"],
      (change) => change.collection === "collections" && change.templateId === options.templateId,
      refresh,
      controller.signal,
      onStatus,
    );
    return { unsubscribe() { active = false; controller.abort(); } };
  }

  private async request<T>(path: string, token: AccessTokenResponse): Promise<T> {
    return this.requestUrl<T>(new URL(path, this.nodeOrigin), token);
  }

  private async requestUrl<T>(url: URL, token: AccessTokenResponse): Promise<T> {
    const response = await this.authorizedFetch(url, token);
    if (!response.ok) throw new Error(await responseMessage(response, "The Vibe node rejected the request."));
    return (await response.json()) as T;
  }

  private async authorizedFetch(input: URL, token: AccessTokenResponse, init: RequestInit = {}): Promise<Response> {
    return this.session.authorizedFetch(input, token, init);
  }

  private startLiveUpdates(
    initialToken: AccessTokenResponse,
    scopes: VibeScope[],
    acceptsChange: (change: Record<string, unknown>) => boolean,
    refresh: () => Promise<void>,
    signal: AbortSignal,
    onStatus?: (status: LiveUpdateStatus) => void,
  ): void {
    let token = initialToken;
    let firstAttempt = true;
    void runLiveEventLoop({
      signal,
      acceptsChange,
      refresh,
      onStatus,
      open: async (attemptSignal) => {
        if (!firstAttempt && await this.resume(scopes)) token = await this.connectMany(scopes);
        firstAttempt = false;
        if (attemptSignal.aborted) throw new DOMException("The operation was aborted.", "AbortError");
        const response = await this.authorizedFetch(new URL("/api/events", this.nodeOrigin), token, {
          headers: { Accept: "text/event-stream" },
          signal: attemptSignal,
        });
        if (!response.ok || !response.body) {
          const message = await responseMessage(response, "Live updates stopped.");
          if (response.status >= 400 && response.status < 500 && ![408, 425, 429].includes(response.status)) {
            throw new LiveEventTerminalError(message);
          }
          throw new Error(message);
        }
        return response.body;
      },
    }).catch((error: unknown) => {
      if (!signal.aborted && !(error instanceof DOMException && error.name === "AbortError")) {
        window.dispatchEvent(new CustomEvent("vibe:error", { detail: error }));
      }
    });
  }
}

async function responseMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { message?: string; error?: string };
    return body.message ?? body.error ?? fallback;
  } catch {
    return fallback;
  }
}
